import { useEffect, useReducer, useCallback, useMemo, useState } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
  GoogleAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithEmailLink,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signInWithCustomToken,
  ApplicationVerifier,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  multiFactor,
  RecaptchaVerifier,
  MultiFactorResolver,
} from 'firebase/auth';
import { getFirestore, collection, doc, getDoc, setDoc } from 'firebase/firestore';
// config
import { FIREBASE_API } from 'src/config-global';
// routes
import { useRouter } from 'src/routes/hook';
import { paths } from 'src/routes/paths';
//
import useFetcher, { endpoints } from 'src/utils/axios';
import { getStorage } from 'firebase/storage';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { AuthContext } from './auth-context';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const firebaseApp = initializeApp(FIREBASE_API);
const AUTH = getAuth(firebaseApp);
const firebaseStorage = getStorage(firebaseApp);
const DB = getFirestore(firebaseApp);

// ----------------------------------------------------------------------
enum Types {
  INITIAL = 'INITIAL',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
};

type Action = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: Action) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { fetchData, postData } = useFetcher();
  const [state, dispatch] = useReducer(reducer, initialState);

  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);

  const router = useRouter();

  const mfaAppName = 'SimpleTranslate'

  const initialize = useCallback(() => {
    try {
      onAuthStateChanged(AUTH, async (user) => {
        if (user) {
          if (user.emailVerified) {
            const userProfile = doc(DB, 'users', user.uid);

            const docSnap = await getDoc(userProfile);

            const profile = docSnap.data();

            dispatch({
              type: Types.INITIAL,
              payload: {
                user: {
                  ...user,
                  ...profile,
                  id: user.uid,
                  role: 'admin',
                },
              },
            });
          } else {
            dispatch({
              type: Types.INITIAL,
              payload: {
                user: null,
              },
            });
          }
        } else {
          dispatch({
            type: Types.INITIAL,
            payload: {
              user: null,
            },
          });
        }
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);
  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    const refreshTokenIfNeeded = async () => {
      if (AUTH.currentUser) {
        const tokenResult = await AUTH.currentUser.getIdTokenResult();
        const expirationTime = tokenResult.expirationTime;
        const currentTime = new Date().getTime();
        const timeToExpiry = new Date(expirationTime).getTime() - currentTime;

        if (timeToExpiry < 5 * 60 * 1000) {
          const newToken = await AUTH.currentUser.getIdToken(true);
          dispatch({
            type: Types.INITIAL,
            payload: {
              user: {
                ...state.user,
                accessToken: newToken,
              },
            },
          });
        }
      }
    };

    refreshTokenIfNeeded();
  }, [state.user, dispatch]);

  // MFA
  const setupTotpEnrollment = useCallback(
    async () => {
      await AUTH.currentUser!.getIdToken(true);

      const mfaSession = await multiFactor(AUTH.currentUser!).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(mfaSession);
      const qrUri = secret.generateQrCodeUrl(
        AUTH.currentUser!.email || '',
        mfaAppName
      );
      if (secret && qrUri) {
        return {secret, qrUri};
      }
      return new Error('There is no Totp Enrollment.');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const enrollUserSecondVerification = useCallback(
    async (type: string, phoneNumber: string, recaptcha: RecaptchaVerifier, returnTo: string | null, params: string | null) => {
      const mfaUser = multiFactor(AUTH.currentUser!);
      const session = await mfaUser.getSession();

      if (type === 'phone') {
        const phoneAuthProvider = new PhoneAuthProvider(AUTH);
        const verificationId = await phoneAuthProvider.verifyPhoneNumber(
          { session, phoneNumber },
          recaptcha
        );
        router.push(paths.auth.firebase.verifyUserCode,
          `type=enrollSms&verificationId=${verificationId}&phoneNumber=${encodeURIComponent(
            phoneNumber)}${returnTo ? `&returnTo=${returnTo}` : ""}${params ? `&params=${params}` : ""}`
        );
      }
      if (type === 'totp') {
        router.push(paths.auth.firebase.verifyUserOtpCode,
          `type=enrollTotp${returnTo ? `&returnTo=${returnTo}` : ""}${params ? `&params=${params}` : ""}`
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  
  const enrollSmsSecondFactor = useCallback(
    async (verificationId: string, smsCode: string, phoneNumber: string) => {
      if (!AUTH.currentUser) {
        throw new Error('No signed in user to enroll MFA on.');
      }
      const cred = PhoneAuthProvider.credential(verificationId, smsCode);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      const mfaUser = multiFactor(AUTH.currentUser!);
      await mfaUser.enroll(assertion, phoneNumber);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const enrollTotpSecondFactor = useCallback(
    async (code: string, totpSecret: any) => {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        code
      );
      await multiFactor(AUTH.currentUser!).enroll(
        assertion,
        mfaAppName
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const checkUserSecondVerification = useCallback(
    async (error: any, recaptcha: RecaptchaVerifier, returnTo: string | null, params: string | null, inviteKey: string | null = null) => {
      const resolver = getMultiFactorResolver(AUTH, error);
      setMfaResolver(resolver);
      const hint = resolver.hints[0];
      switch (hint.factorId) {
        case PhoneMultiFactorGenerator.FACTOR_ID:
          sendUserSmsCode(resolver, recaptcha, inviteKey, returnTo, params);
          break;
        case TotpMultiFactorGenerator.FACTOR_ID:
          sendUserTotpCode(resolver, inviteKey, returnTo, params);
          break;
        default:
          console.warn('Unsupported MFA factor:', hint.factorId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const sendUserTotpCode = useCallback(
    async (resolver: MultiFactorResolver, returnTo: string | null, params: string | null, inviteKey: string | null = null) => {
        const hint = resolver.hints[0];
        const encodedHintUid = encodeURIComponent(hint.uid)
  
        router.push(paths.auth.firebase.verifyUserOtpCode,
          `type=totp&hintUid=${encodedHintUid}${inviteKey ? `&inviteKey=${inviteKey}` : ""}${returnTo ? `&returnTo=${returnTo}` : ""}${params ? `&params=${params}` : ""}`
        );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const sendUserSmsCode = useCallback(
    async (resolver: MultiFactorResolver, recaptcha: RecaptchaVerifier, returnTo: string | null, params: string | null, inviteKey: string | null = null) => {
      const phoneInfoOptions = {
        multiFactorHint: resolver.hints[0],
        session: resolver.session
      };
      const phoneAuthProvider = new PhoneAuthProvider(AUTH);

      phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptcha)
      .then((verificationId) => {
        router.push(paths.auth.firebase.verifyUserCode,
          `type=phone&verificationId=${verificationId}${inviteKey ? `&inviteKey=${inviteKey}` : ""}${returnTo ? `&returnTo=${returnTo}` : ""}${params ? `&params=${params}` : ""}`
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const checkTotpVerifyCode = useCallback(
    async (code: string, hintUid: string | null, inviteKey: string | null = null) => {
      if (!mfaResolver) throw new Error('No multi-factor session. Please request a new code.');
      if (!hintUid) throw new Error('Missing TOTP hint identifier.');

      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        mfaResolver.hints[0].uid,
        code
      );
      await mfaResolver.resolveSignIn(assertion);
      if (inviteKey) {
        fetchData([
          endpoints.users.invitation_response,
          {
            params: {
              inviteKey,
              accepted: true,
            },
          },
        ]);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mfaResolver]
  );

  const checkSmsVerifyCode = useCallback(
    async (code: string, verificationId: string | null, inviteKey: string | null = null) => {
      router.push(paths.dashboard.root);
      if (!mfaResolver) throw new Error('No multi-factor session. Please request a new code.');

      if (verificationId) {
        const cred = PhoneAuthProvider.credential(verificationId, code);
        const assertion = PhoneMultiFactorGenerator.assertion(cred);
        await mfaResolver.resolveSignIn(assertion);
        if (inviteKey) {
          fetchData([
            endpoints.users.invitation_response,
            {
              params: {
                inviteKey,
                accepted: true,
              },
            },
          ]);
        };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mfaResolver]
  );

  // MFA RECOVERY
  const secondFactorRecovery = useCallback(
    async (email: string) => {
      postData(endpoints.users.secondFactorRecovery, {email});
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []
  );

  // LOGIN
  const login = useCallback(
    async (email: string, password: string, inviteKey: string | null = null) => {
      if (inviteKey === null) {
        await signInWithEmailAndPassword(AUTH, email, password);
      } else {
        await signInWithEmailAndPassword(AUTH, email, password);
        fetchData([
          endpoints.users.invitation_response,
          {
            params: {
              inviteKey,
              accepted: true,
            },
          },
        ]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // CHANGE PASSWORD
  const change_password = useCallback(async (oldPassword: string, newPassword: string) => {
    const user = AUTH.currentUser;
    if (user && user.email) {
      const credential = EmailAuthProvider.credential(user.email, oldPassword);
      reauthenticateWithCredential(user, credential)
        .then(() => {
          updatePassword(user, newPassword);
        })
        .catch((error) => {
          console.log(error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const loginWithGoogle = useCallback(async (inviteKey: string | null = null) => {
    const provider = new GoogleAuthProvider();

    await signInWithPopup(AUTH, provider).then((result) => {
      postData(
        endpoints.users.login_with_provider, {
        user: result.user,
        },
      );
      if (inviteKey !== null) {
        fetchData([
          endpoints.users.invitation_response,
          {
            params: {
              inviteKey,
              accepted: true,
            },
          },
        ]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postData])

  const loginWithSalesforce = useCallback(async (token: string) => {
    await signInWithCustomToken(AUTH, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // REGISTER
  const register = useCallback(async (email: string, password: string, firstName: string, lastName: string) =>
    postData(endpoints.users.register_user, {
      email,
      displayName: `${firstName} ${lastName}`,
      password,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postData]
  );
  const register_with_invite = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      inviteKey: string
    ) => {
      postData(endpoints.users.register_user, {
        email,
        displayName: `${firstName} ${lastName}`,
        password,
        inviteKey,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  // LOGOUT
  const logout = useCallback(async () => {
    await signOut(AUTH);
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email: string) => {
    postData(endpoints.users.password_reset, {email});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postData]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user?.emailVerified ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      db: DB,
      currentUser: AUTH.currentUser,
      user: state.user,
      method: 'firebase',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      firebaseStorage,
      //
      setupTotpEnrollment,
      enrollUserSecondVerification,
      enrollTotpSecondFactor,
      enrollSmsSecondFactor,
      checkUserSecondVerification,
      checkSmsVerifyCode,
      checkTotpVerifyCode,
      change_password,
      secondFactorRecovery,
      login,
      register_with_invite,
      logout,
      register,
      forgotPassword,
      loginWithGoogle,
      loginWithSalesforce,
    }),
    [
      status,
      state.user,

      //
      setupTotpEnrollment,
      enrollUserSecondVerification,
      enrollTotpSecondFactor,
      enrollSmsSecondFactor,
      checkUserSecondVerification,
      checkSmsVerifyCode,
      checkTotpVerifyCode,
      change_password,
      secondFactorRecovery,
      register_with_invite,
      login,
      logout,
      register,
      forgotPassword,
      loginWithGoogle,
      loginWithSalesforce
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}